import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import {
  addUserEmail,
  deleteUserEmail,
  promoteUserEmail,
  setPreferredEmail,
} from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import { SharedCardLayout } from './layout'
import VerifyEmailDialog from './VerifyEmailDialog'

// Sentinel for "use my primary email" — shadcn's Select can't carry null.
const PRIMARY = 'primary'

/**
 * Email addresses section (RSCX-351): manage the additional addresses tied to
 * this account (any of them can log in), and pick which address each of your
 * accounts' notifications go to.
 */
const EmailAddressesSection = () => {
  const { user } = useContext(UserStoreContext)
  const queryClient = useQueryClient()

  const emails = user.emailAddresses ?? []
  const verifiedEmails = emails.filter((address) => address.verified)
  const roles = user.entityRoles ?? []
  const [verifyingEmail, setVerifyingEmail] = useState(null)

  const form = useForm({ defaultValues: { email: '' } })

  const refreshUser = () => queryClient.invalidateQueries({ queryKey: ['users', user.id] })

  const { mutate: addEmail, isPending: adding } = useMutation({
    mutationFn: (email) => addUserEmail({ userId: user.id, email }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`Verification code sent to ${data.email}`, 'success')
      form.reset({ email: '' })
      refreshUser()
      // Straight into the code entry — the address is unusable until verified.
      setVerifyingEmail(data)
    },
  })

  const { mutate: removeEmail } = useMutation({
    mutationFn: (emailId) => deleteUserEmail({ userId: user.id, emailId }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Email address removed', 'success')
      refreshUser()
    },
  })

  const { mutate: makePrimary, isPending: promoting } = useMutation({
    mutationFn: (emailId) => promoteUserEmail({ userId: user.id, emailId }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`${data.email} is now your primary email`, 'success')
      refreshUser()
    },
  })

  const { mutate: savePreferred } = useMutation({
    mutationFn: ({ entityId, emailId }) =>
      setPreferredEmail({ userId: user.id, entityId, emailId }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Notification email updated', 'success')
      refreshUser()
    },
  })

  return (
    <SharedCardLayout
      title="Email Addresses"
      description="You can sign in with any of these addresses, and your login code is sent to whichever one you use. Your primary email identifies your account and can't be removed — make another address primary first."
    >
      <SharedCardLayout.Form
        formProps={form}
        onSubmit={form.handleSubmit((d) => addEmail(d.email))}
      >
        <SharedCardLayout.Fields className="flex flex-col gap-6">
          <ul className="divide-accent divide-y rounded border">
            <li className="flex items-center justify-between px-4 py-2">
              <span className="text-sm font-medium">{user.email}</span>
              <Badge variant="secondary">Primary</Badge>
            </li>
            {emails.map((address) => (
              <li key={address.id} className="flex items-center justify-between px-4 py-2">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm">{address.email}</span>
                  {!address.verified && <Badge variant="outline">Unverified</Badge>}
                </div>
                <div className="flex flex-row items-center gap-1">
                  {address.verified ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={promoting}
                      onClick={() => makePrimary(address.id)}
                    >
                      Make Primary
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setVerifyingEmail(address)}
                    >
                      Verify
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${address.email}`}
                    onClick={() => removeEmail(address.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-row items-end gap-2">
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: 'Enter an email address',
                pattern: { value: /.+@.+\..+/, message: 'Enter a valid email address' },
              }}
              render={({ field }) => (
                <FormItem className="max-w-sm flex-1">
                  <FormLabel>Add another email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="outline" loading={adding}>
              Add Email
            </Button>
          </div>

          {roles.length > 0 && verifiedEmails.length > 0 && (
            <div className="flex flex-col gap-3">
              <div>
                <h4 className="text-sm font-semibold">Account email</h4>
                <p className="text-muted-foreground text-sm">
                  Choose the address each account knows you by — it&apos;s what members of that
                  account see and where its notifications are sent. Accounts left on your primary
                  address follow it if you make a different address primary.
                </p>
              </div>
              {roles.map((role) => (
                <div key={role.id} className="flex items-center justify-between gap-4">
                  <span className="text-sm">{role.entity?.name ?? 'Account'}</span>
                  <Select
                    value={role.preferredEmail ?? PRIMARY}
                    onValueChange={(value) =>
                      savePreferred({
                        entityId: role.entity?.id ?? role.entity,
                        emailId: value === PRIMARY ? null : value,
                      })
                    }
                  >
                    <SelectTrigger className="w-72">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PRIMARY}>{user.email} (primary)</SelectItem>
                      {verifiedEmails.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          {address.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </SharedCardLayout.Fields>
      </SharedCardLayout.Form>

      {verifyingEmail && (
        <VerifyEmailDialog
          userId={user.id}
          email={verifyingEmail}
          onClose={() => setVerifyingEmail(null)}
          onVerified={() => {
            setVerifyingEmail(null)
            refreshUser()
          }}
        />
      )}
    </SharedCardLayout>
  )
}

export default EmailAddressesSection
